'use client';

import React from 'react';
import { 
  ShoppingBag, 
  ChevronDown, 
  ChevronRight, 
  Edit3, 
  Trash2, 
  Plus, 
  Minus, 
  Barcode, 
  Tag, 
  AlertTriangle 
} from 'lucide-react';

export interface ProductItem {
  id: string;
  name: string;
  brand: string;
  category: string;
  description?: string;
  sku: string | null;
  barcode: string | null;
  costPrice: number;
  salePrice: number;
  stock: number;
  minStock: number;
  commissionRate: number;
  active: boolean;
  imageUrl?: string | null;
}

interface ProductsCategoryListProps {
  products: ProductItem[];
  groupBy: 'CATEGORY' | 'BRAND';
  loading: boolean;
  onEditProduct: (product: ProductItem) => void;
  onOpenNewProduct: () => void;
  onQuickAdjustStock: (product: ProductItem, delta: number) => void;
}

export default function ProductsCategoryList({
  products,
  groupBy,
  loading,
  onEditProduct,
  onOpenNewProduct,
  onQuickAdjustStock
}: ProductsCategoryListProps) {
  if (loading) {
    return (
      <div className="bg-white border border-slate-200/90 rounded-3xl p-16 text-center shadow-sm">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm font-medium text-slate-500">Cargando catálogo en vivo...</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="bg-white border border-slate-200/90 rounded-3xl p-16 text-center shadow-sm flex flex-col items-center justify-center min-h-[340px]">
        <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 mb-4">
          <ShoppingBag className="w-8 h-8 stroke-[1.2]" />
        </div>
        <h3 className="text-base md:text-lg font-bold text-slate-800">
          No se encontraron productos.
        </h3>
        <p className="text-xs md:text-sm text-slate-400 mt-1 max-w-sm">
          Importá un Excel o creá tu primer producto.
        </p>
        <button
          onClick={onOpenNewProduct}
          className="mt-5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-sm transition transform active:scale-95"
        >
          + Crear primer producto
        </button>
      </div>
    );
  }

  // Agrupar productos
  const groups: Record<string, ProductItem[]> = {};
  products.forEach((p) => {
    const key = groupBy === 'CATEGORY' ? (p.category || 'Sin categoría') : (p.brand || 'Sin marca');
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  });

  return (
    <div className="space-y-4">
      {Object.entries(groups).map(([groupName, groupProducts]) => (
        <div 
          key={groupName}
          className="bg-white border border-slate-200/90 rounded-3xl overflow-hidden shadow-sm transition"
        >
          {/* Cabecera del Grupo */}
          <div className="p-4 px-6 bg-slate-50/70 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-sm text-slate-900">
                {groupName}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-200/70 text-slate-700">
                {groupProducts.length}
              </span>
            </div>
            <span className="text-xs text-slate-400 font-medium">
              {groupProducts.reduce((acc, p) => acc + p.stock, 0)} unidades en stock
            </span>
          </div>

          {/* Listado de Productos en la Categoría */}
          <div className="divide-y divide-slate-100">
            {groupProducts.map((product) => {
              const margin = product.costPrice > 0 
                ? Math.round(((product.salePrice - product.costPrice) / product.costPrice) * 100)
                : 100;

              return (
                <div 
                  key={product.id}
                  className="p-4 px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/50 transition group"
                >
                  {/* Foto y Datos del Producto */}
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700 shrink-0 overflow-hidden">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <ShoppingBag className="w-6 h-6 stroke-[1.5]" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-sm text-slate-900 truncate flex items-center gap-2">
                        <span>{product.name}</span>
                        {!product.active && (
                          <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-slate-100 text-slate-500">
                            Pausado
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-400 mt-0.5 font-mono">
                        {product.brand && (
                          <span className="text-slate-600 font-sans font-semibold">
                            {product.brand}
                          </span>
                        )}
                        {product.sku && <span>SKU: {product.sku}</span>}
                        {product.barcode && (
                          <span className="flex items-center gap-1">
                            <Barcode className="w-3.5 h-3.5" />
                            {product.barcode}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Precios, Márgenes y Control de Stock */}
                  <div className="flex flex-wrap items-center justify-between sm:justify-end gap-5 shrink-0">
                    {/* Precios */}
                    <div className="text-right">
                      <div className="text-sm font-black text-slate-900">
                        ${product.salePrice.toLocaleString('es-AR')}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        Costo: ${product.costPrice.toLocaleString('es-AR')}{' '}
                        <span className="text-emerald-600 font-semibold">({margin}% marg.)</span>
                      </div>
                    </div>

                    {/* Stock y Botones de Ajuste Rápido */}
                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200/90 rounded-2xl px-2.5 py-1">
                      <button
                        onClick={() => onQuickAdjustStock(product, -1)}
                        disabled={product.stock <= 0}
                        className="p-1 rounded-lg hover:bg-slate-200/80 text-slate-500 disabled:opacity-30 transition"
                        title="Deducir 1 unidad"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className={`text-xs font-extrabold min-w-[44px] text-center ${
                        product.stock <= 0
                          ? 'text-rose-600'
                          : product.stock <= product.minStock
                          ? 'text-amber-600'
                          : 'text-emerald-700'
                      }`}>
                        {product.stock} un.
                      </span>
                      <button
                        onClick={() => onQuickAdjustStock(product, 1)}
                        className="p-1 rounded-lg hover:bg-slate-200/80 text-slate-500 transition"
                        title="Sumar 1 unidad"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Botón Editar */}
                    <button
                      onClick={() => onEditProduct(product)}
                      className="p-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-900 shadow-sm transition"
                      title="Editar ficha completa del producto"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
